import React from 'react';
import { useTranslation } from 'react-i18next';

import { FirmwareInfo, copyFirmwareDescriptor } from '@/features/firmware/firmwaresSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';
import Dropdown from '@/components/Common/Dropdown';

import { CatHeader } from '@/pages/Projects/Project';

export interface FirmwareProps {
  index:        number;
  firmware:     FirmwareInfo | null;
  setFirmware:  (firmware: FirmwareInfo) => any;
  onClose:      () => any;
}

// `update.url` is a single string on the wire (Task-32 §"POST /firmwares.json"),
// so a webOS config carries its two downloads — the .epk and the micom .hex —
// comma-separated inside it:
//   "url": "http://…/…_SECURED.epk,http://…/micom_v3.19.0.hex"
const URL_SEPARATOR = ',';

// Only webOS takes a second URL; every other platform ships one binary.
const WEBOS_URL_LIMIT = 2;
const DEFAULT_URL_LIMIT = 1;

function urlLimit(platform: string): number {
  return platform.trim().toLowerCase() == 'webos'
         ?	WEBOS_URL_LIMIT
         :	DEFAULT_URL_LIMIT;
}

function splitUrls(value: string): string[] {
  return value.split(URL_SEPARATOR)
              .map((url: string) => url.trim())
              .filter((url: string) => url != '');
}

// What `input` adds to `urls`, de-duplicated and capped at `limit`. Returns
// `urls` itself when it adds nothing, so callers can skip a no-op update.
function mergeUrls(urls: string[], input: string, limit: number): string[] {
  const room = limit - urls.length;
  if (room <= 0)
    return urls;

  // Split on the separator here too, so both webOS URLs can be pasted at once
  const added = splitUrls(input)
                  .filter((url: string) => !urls.includes(url))
                  .slice(0, room);

  return added.length == 0
         ?	urls
         :	[...urls, ...added];
}

const Firmware = (props: FirmwareProps) => {
  const { t } = useTranslation();

  const isNew = props.index == -2;

  const [firmware, setFirmware] = React.useState(null as FirmwareInfo | null);
  const [changes, setChanges] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);
  const [versionInput, setVersionInput] = React.useState('');
  // URLs are typed into a staging input and committed with Enter, the same way
  // versions are — what is committed lives on firmware.update.url and is shown
  // as chips under the field, so the input itself always starts empty.
  const [urlInput, setUrlInput] = React.useState('');

  React.useEffect(() => {
    setFirmware(props.firmware);
    setVersionInput('');
    setUrlInput('');
  }, [props.firmware]);

  const platformOptions = React.useMemo(() => [
    { value: "tizen", desc: "Tizen" },
    { value: "webos", desc: "WebOS" },
    { value: "android", desc: "Android" },
    { value: "philips", desc: "Philips" },
  ], []);

  const platformInvalid = React.useMemo(() => {
    return !firmware || firmware.platform.trim() == '';
  }, [firmware]);

  const modelInvalid = React.useMemo(() => {
    return !firmware || firmware.model.trim() == '';
  }, [firmware]);

  const versionsInvalid = React.useMemo(() => {
    return !firmware || firmware.versions.length == 0;
  }, [firmware]);

  const updateVersionInvalid = React.useMemo(() => {
    return !firmware || !firmware.update || firmware.update.version.trim() == '';
  }, [firmware]);

  const updateFilenameInvalid = React.useMemo(() => {
    return !firmware || !firmware.update || firmware.update.filename.trim() == '';
  }, [firmware]);

  const updateUrlInvalid = React.useMemo(() => {
    return !firmware || !firmware.update || firmware.update.url.trim() == '';
  }, [firmware]);

  const updateIdInvalid = React.useMemo(() => {
    return !firmware || !firmware.update || firmware.update.id.trim() == '';
  }, [firmware]);

  const updateUrls = React.useMemo(() => {
    return firmware && firmware.update
           ?	splitUrls(firmware.update.url)
           :	[];
  }, [firmware]);

  const maxUrls = React.useMemo(() => {
    return firmware
           ?	urlLimit(firmware.platform)
           :	DEFAULT_URL_LIMIT;
  }, [firmware]);

  const updatePlatform = React.useCallback((val: string) => {
    if (!firmware)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.platform = val;
    // Leaving webOS drops the second URL rather than saving a config the new
    // platform has no use for — the chip disappears as the platform changes.
    nfirmware.update.url = updateUrls.slice(0, urlLimit(val)).join(URL_SEPARATOR);

    setFirmware(nfirmware);
    setChanges(true);
  }, [firmware, updateUrls, setFirmware]);

  const updateModel = React.useCallback((val: string) => {
    if (!firmware)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.model = val;

    setFirmware(nfirmware);
    setChanges(true);
  }, [firmware, setFirmware]);

  const addVersion = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !firmware)
      return;

    // Split by comma and add each version separately
    const versions = versionInput.split(',')
      .map(v => v.trim())
      .filter(v => v !== '' && !firmware.versions.includes(v));

    if (versions.length === 0)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.versions = [...nfirmware.versions, ...versions];

    setFirmware(nfirmware);
    setChanges(true);
    
    setVersionInput('');
  }, [firmware, versionInput, setFirmware]);

  const removeVersion = React.useCallback((version: string) => {
    if (!firmware)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.versions = nfirmware.versions.filter(v => v !== version);

    setFirmware(nfirmware);
    setChanges(true);
  }, [firmware, setFirmware]);

  const updateUpdateVersion = React.useCallback((val: string) => {
    if (!firmware)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.update.version = val;

    setFirmware(nfirmware);
    setChanges(true);
  }, [firmware, setFirmware]);

  const updateUpdateFilename = React.useCallback((val: string) => {
    if (!firmware)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.update.filename = val;

    setFirmware(nfirmware);
    setChanges(true);
  }, [firmware, setFirmware]);

  const addUrl = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !firmware)
      return;

    const urls = mergeUrls(updateUrls, urlInput, maxUrls);
    if (urls === updateUrls)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.update.url = urls.join(URL_SEPARATOR);

    setFirmware(nfirmware);
    setChanges(true);

    setUrlInput('');
  }, [firmware, urlInput, updateUrls, maxUrls, setFirmware]);

  const removeUrl = React.useCallback((url: string) => {
    if (!firmware)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.update.url = updateUrls.filter((u: string) => u !== url)
                                     .join(URL_SEPARATOR);

    setFirmware(nfirmware);
    setChanges(true);
  }, [firmware, updateUrls, setFirmware]);

  const updateUpdateFilesize = React.useCallback((val: string) => {
    if (!firmware)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.update.filesize = parseInt(val) || 0;

    setFirmware(nfirmware);
    setChanges(true);
  }, [firmware, setFirmware]);

  const updateUpdateId = React.useCallback((val: string) => {
    if (!firmware)
      return;

    let nfirmware = copyFirmwareDescriptor(firmware);
    nfirmware.update.id = val;

    setFirmware(nfirmware);
    setChanges(true);
  }, [firmware, setFirmware]);

  const handleSave = React.useCallback(() => {
    if (!firmware) {
      setTriedSave(true);
      return;
    }

    // Auto-add version and URL from their inputs if user forgot to press Enter
    let firmwareToSave = firmware;
    let staged = false;
    const pendingVersion = versionInput.trim();
    if (pendingVersion !== '' && !firmware.versions.includes(pendingVersion)) {
      firmwareToSave = copyFirmwareDescriptor(firmware);
      firmwareToSave.versions = [...firmwareToSave.versions, pendingVersion];
      staged = true;
      setVersionInput('');
    }

    const pendingUrls = mergeUrls(updateUrls, urlInput, maxUrls);
    if (pendingUrls !== updateUrls) {
      if (!staged)
        firmwareToSave = copyFirmwareDescriptor(firmware);
      firmwareToSave.update.url = pendingUrls.join(URL_SEPARATOR);
      staged = true;
      setUrlInput('');
    }

    if (staged)
      setFirmware(firmwareToSave);

    // Check validation with potentially updated firmware
    const versionsValid = firmwareToSave.versions.length > 0;
    const urlValid = firmwareToSave.update.url.trim() !== '';

    if (platformInvalid || modelInvalid || !versionsValid || !urlValid ||
        updateIdInvalid || updateVersionInvalid || updateFilenameInvalid) {
      setTriedSave(true);
      return;
    }

    setTriedSave(false);
    props.setFirmware(firmwareToSave);
    setChanges(false);
    props.onClose();
  }, [firmware, versionInput, urlInput, updateUrls, maxUrls, props.setFirmware,
      platformInvalid, modelInvalid, updateIdInvalid, updateVersionInvalid,
      updateFilenameInvalid]);

  if (!firmware)
    return null;

  return (
    <React.Fragment>
      <FlexRow direction="column"
               align="start"
               className="px-[32px]">
        <FlexRow direction="column"
                 align="start"
                 className="py-0 pb-[100px] gap-[16px] max-w-[620px]">
          <CatHeader caption={isNew ? 'Firmware:new_firmware' : 'Firmware:edit_firmware'} />

          <Dropdown label={t('Firmware:platform') + '*'}
                    value={firmware.platform}
                    options={platformOptions}
                    isInvalid={triedSave && platformInvalid}
                    errorMessage={t('Firmware:platform_invalid')}
                    setValue={updatePlatform}>
            <></>
          </Dropdown>

          <Input label={t('Firmware:model') + '*'}
                 isInvalid={triedSave && modelInvalid}
                 errorMessage={t('Firmware:model_invalid')}
                 value={firmware.model}
                 onValueChange={updateModel} />

          <div className="w-full">
            <Input label={t('Firmware:versions') + '*'}
                   isInvalid={triedSave && versionsInvalid}
                   errorMessage={t('Firmware:versions_invalid')}
                   value={versionInput}
                   placeholder={t('Firmware:versions_placeholder')}
                   onValueChange={setVersionInput}
                   onKeyDown={addVersion} />
            {firmware.versions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2"
                   onFocusCapture={(e) => e.stopPropagation()}>
                {firmware.versions.map((version, idx) => (
                  <div key={idx}
                       className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                    <span>{version}</span>
                    <span className="ml-1 hover:bg-primary-200 rounded-full p-0.5 cursor-pointer"
                          tabIndex={-1}
                          role="button"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            removeVersion(version);
                          }}>
                      <span className="material-icons !text-[14px]">close</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <CatHeader caption="Firmware:update_section" />

          <Input label={t('Firmware:update_id') + '*'}
                 isInvalid={triedSave && updateIdInvalid}
                 errorMessage={t('Firmware:update_id_invalid')}
                 value={firmware.update.id}
                 onValueChange={updateUpdateId} />

          <Input label={t('Firmware:update_version') + '*'}
                 isInvalid={triedSave && updateVersionInvalid}
                 errorMessage={t('Firmware:update_version_invalid')}
                 value={firmware.update.version}
                 onValueChange={updateUpdateVersion} />

          <Input label={t('Firmware:update_filename') + '*'}
                 isInvalid={triedSave && updateFilenameInvalid}
                 errorMessage={t('Firmware:update_filename_invalid')}
                 value={firmware.update.filename}
                 onValueChange={updateUpdateFilename} />

          <div className="w-full">
            <Input label={t('Firmware:update_url') + '*'}
                   isInvalid={triedSave && updateUrlInvalid}
                   errorMessage={t('Firmware:update_url_invalid')}
                   value={urlInput}
                   isDisabled={updateUrls.length >= maxUrls}
                   placeholder={maxUrls > 1
                                ?	t('Firmware:update_url_placeholder_webos')
                                :	t('Firmware:update_url_placeholder')}
                   onValueChange={setUrlInput}
                   onKeyDown={addUrl} />
            {updateUrls.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2"
                   onFocusCapture={(e) => e.stopPropagation()}>
                {updateUrls.map((url, idx) => (
                  <div key={idx}
                       className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm max-w-full">
                    <span className="break-all">{url}</span>
                    <span className="ml-1 hover:bg-primary-200 rounded-full p-0.5 cursor-pointer"
                          tabIndex={-1}
                          role="button"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            removeUrl(url);
                          }}>
                      <span className="material-icons !text-[14px]">close</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Input label={t('Firmware:update_filesize')}
                 value={firmware.update.filesize.toString()}
                 onValueChange={updateUpdateFilesize}
                 type="number" />
        </FlexRow>
      </FlexRow>
      <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]">
        <Button className="uppercase"
                caption={t('Firmware:cancel')}
                onClick={() => props.onClose()}
                layout="white" />
        <Button caption={t('Firmware:save')}
                icon="save"
                isDisabled={!changes || (triedSave && (platformInvalid || modelInvalid || versionsInvalid ||
                           updateIdInvalid || updateVersionInvalid || updateFilenameInvalid || updateUrlInvalid))}
                onClick={() => handleSave()} />
      </FlexRow>
    </React.Fragment>
  );
};

export default Firmware;
